import React, {
  memo,
  useEffect,
  useRef,
  useState,
} from "react";

import nmp_mapboxgl from "@neshan-maps-platform/mapbox-gl";
import "@neshan-maps-platform/mapbox-gl/dist/NeshanMapboxGl.css";

const DEFAULT_CENTER = [51.389, 35.6892];
const DEFAULT_ZOOM = 14;

const MAP_KEY = String(
  process.env.REACT_APP_NESHAN_MAP_KEY || ""
).trim();

const normalizeCenter = (center) => {
  if (!Array.isArray(center) || center.length !== 2) {
    return DEFAULT_CENTER;
  }

  const lng = Number(center[0]);
  const lat = Number(center[1]);

  const isValid =
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    lng >= -180 &&
    lng <= 180 &&
    lat >= -90 &&
    lat <= 90;

  return isValid ? [lng, lat] : DEFAULT_CENTER;
};

const normalizeZoom = (zoom) => {
  const numericZoom = Number(zoom);

  if (!Number.isFinite(numericZoom)) {
    return DEFAULT_ZOOM;
  }

  return Math.min(Math.max(numericZoom, 1), 22);
};

function NeshanMap({
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  onChange,
  readOnly = false,
  className = "",
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const initializationTimerRef = useRef(null);

  const onChangeRef = useRef(onChange);
  const readOnlyRef = useRef(readOnly);

  const [isLoading, setIsLoading] = useState(true);
  const [mapError, setMapError] = useState("");

  /*
   * آخرین نسخه callback را نگه می‌داریم تا تغییر reference
   * تابع onChange باعث تخریب و بازسازی نقشه نشود.
   */
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);

  /*
   * ساخت نقشه فقط یک مرتبه در زمان mount.
   */
  useEffect(() => {
    const container = mapContainerRef.current;

    if (!container) {
      return undefined;
    }

    if (!MAP_KEY) {
      const message =
        "کلید نقشه نشان تنظیم نشده است. متغیر REACT_APP_NESHAN_MAP_KEY را بررسی کنید.";

      console.error(message);
      setMapError(message);
      setIsLoading(false);

      return undefined;
    }

    let isDisposed = false;
    let map = null;
    let marker = null;

    const initialCenter = normalizeCenter(center);
    const initialZoom = normalizeZoom(zoom);

    const initializeMap = () => {
      if (
        isDisposed ||
        !mapContainerRef.current ||
        mapRef.current
      ) {
        return;
      }

      try {
        setIsLoading(true);
        setMapError("");

        /*
         * مهم:
         * mapType و mapKey مربوط به API واقعی پکیج نصب‌شده شما هستند.
         * آن‌ها را با style و key جایگزین نکنید.
         */
        map = new nmp_mapboxgl.Map({
          container: mapContainerRef.current,

          mapType: nmp_mapboxgl.Map.mapTypes.neshanVector,

          mapKey: MAP_KEY,

          center: initialCenter,
          zoom: initialZoom,

          /*
           * POI یک‌سری لایه و اطلاعات اضافه بارگیری می‌کند.
           * اگر لازم ندارید false سریع‌تر است.
           */
          poi: false,

          traffic: false,
          trackResize: true,
          interactive: !readOnlyRef.current,
        });

        mapRef.current = map;

        marker = new nmp_mapboxgl.Marker({
          draggable: !readOnlyRef.current,
          color: readOnlyRef.current
            ? "#ef4444"
            : "#1d4ed8",
        })
          .setLngLat(initialCenter)
          .addTo(map);

        markerRef.current = marker;

        const handleMapClick = (event) => {
          if (readOnlyRef.current) {
            return;
          }

          const lng = Number(event.lngLat.lng);
          const lat = Number(event.lngLat.lat);

          if (
            !Number.isFinite(lng) ||
            !Number.isFinite(lat)
          ) {
            return;
          }

          markerRef.current?.setLngLat([lng, lat]);

          onChangeRef.current?.([lng, lat]);

          /*
           * حرکت کوتاه و سریع‌تر از flyTo پیش‌فرض.
           */
          mapRef.current?.easeTo({
            center: [lng, lat],
            duration: 250,
          });
        };

        const handleMarkerDragEnd = () => {
          if (
            readOnlyRef.current ||
            !markerRef.current
          ) {
            return;
          }

          const lngLat = markerRef.current.getLngLat();

          onChangeRef.current?.([
            Number(lngLat.lng),
            Number(lngLat.lat),
          ]);
        };

        const handleLoad = () => {
          if (isDisposed) {
            return;
          }

          setIsLoading(false);

          requestAnimationFrame(() => {
            mapRef.current?.resize();
          });
        };

        const handleError = (event) => {
          /*
           * بعضی خطاهای مربوط به یک tile نباید کل نقشه را مخفی کنند؛
           * بنابراین آن‌ها را در Console نگه می‌داریم.
           */
          console.error(
            "Neshan map resource error:",
            event?.error || event
          );

          if (!mapRef.current) {
            setMapError("خطا در بارگذاری نقشه نشان");
            setIsLoading(false);
          }
        };

        map.on("load", handleLoad);
        map.on("error", handleError);

        if (!readOnlyRef.current) {
          map.on("click", handleMapClick);
          marker.on("dragend", handleMarkerDragEnd);
        }

        /*
         * وقتی اندازه مودال، grid یا container تغییر کند،
         * نقشه بدون timeoutهای متعدد resize می‌شود.
         */
        if (typeof ResizeObserver !== "undefined") {
          resizeObserverRef.current = new ResizeObserver(
            () => {
              if (!mapRef.current) {
                return;
              }

              requestAnimationFrame(() => {
                mapRef.current?.resize();
              });
            }
          );

          resizeObserverRef.current.observe(
            mapContainerRef.current
          );
        }

        /*
         * برای زمانی که کانتینر درون مودال انیمیشن دارد.
         */
        requestAnimationFrame(() => {
          mapRef.current?.resize();
        });
      } catch (error) {
        console.error(
          "Neshan map initialization error:",
          error
        );

        setMapError(
          "راه‌اندازی نقشه نشان با خطا مواجه شد."
        );

        setIsLoading(false);
      }
    };

    /*
     * در React 18 StrictMode، effect در محیط development
     * به‌صورت setup → cleanup → setup اجرا می‌شود.
     *
     * زمان‌بندی صفر میلی‌ثانیه باعث می‌شود initialization مربوط
     * به اجرای آزمایشی اول، پیش از اجرا cancel شود؛ در نتیجه
     * درخواست‌های نقشه دو بار ارسال نمی‌شوند.
     */
    initializationTimerRef.current = window.setTimeout(
      initializeMap,
      0
    );

    return () => {
      isDisposed = true;

      if (initializationTimerRef.current !== null) {
        window.clearTimeout(
          initializationTimerRef.current
        );

        initializationTimerRef.current = null;
      }

      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      /*
       * map.remove تمام event listenerهای داخلی map را نیز
       * پاک می‌کند.
       */
      if (mapRef.current) {
        mapRef.current.remove();
      }

      markerRef.current = null;
      mapRef.current = null;
    };

    /*
     * این effect نباید با تغییر center، zoom یا onChange
     * مجدداً اجرا شود.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * تغییر مختصات ورودی، بدون بازسازی نقشه.
   */
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;

    if (!map || !marker) {
      return;
    }

    const nextCenter = normalizeCenter(center);
    const nextZoom = normalizeZoom(zoom);

    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();

    const centerChanged =
      Math.abs(currentCenter.lng - nextCenter[0]) >
        0.000001 ||
      Math.abs(currentCenter.lat - nextCenter[1]) >
        0.000001;

    const zoomChanged =
      Math.abs(currentZoom - nextZoom) > 0.01;

    marker.setLngLat(nextCenter);

    if (centerChanged || zoomChanged) {
      map.easeTo({
        center: nextCenter,
        zoom: nextZoom,
        duration: 300,
        essential: true,
      });
    }
  }, [center?.[0], center?.[1], zoom]);

  return (
    <div
      className={[
        "relative h-full min-h-[300px] w-full",
        "overflow-hidden rounded-lg bg-gray-100",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        ref={mapContainerRef}
        className="absolute inset-0 h-full w-full"
      />

      {isLoading && !mapError && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
          <div className="flex flex-col items-center gap-3">
            <span className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-700" />

            <span className="text-xs font-medium text-gray-600">
              در حال بارگذاری نقشه...
            </span>
          </div>
        </div>
      )}

      {mapError && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-red-50 p-4">
          <p className="text-center text-sm text-red-700">
            {mapError}
          </p>
        </div>
      )}
    </div>
  );
}

export default memo(NeshanMap);
